/**
 * 設定 `/settings`。
 *
 * タブ: 一般 / ACME / 通知 / Webメール連携 / 配信既定値 / アカウント
 *
 * プラットフォーム設定は `ops.settings.write`（superadmin のみ）。
 * アカウントタブだけは全ロールが自分の 2FA・パスワード・セッションを扱えるので、
 * 権限が無くてもタブ自体は出す。
 */
import * as React from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, KeyRound, Lock, Palette, Rocket, Settings2, UserRound } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Input,
  PageHeader,
  Section,
  StatusBadge,
  SwitchRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ThemeToggle,
  formatDateTime,
  formatRelative,
} from '@mail-sv/ui';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import {
  changePassword,
  fetchSessions,
  revokeOtherSessions,
  setupTotp,
  verifyTotpSetup,
  type SessionRow,
  type TotpSetup,
} from '../lib/auth.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { saveOpsSettings, toOpsSettings, type ApiSettingsList } from '../lib/settings-view.js';
import { useAuth, usePermission } from '../stores/auth.js';
import { PageTabs } from '../components/page-tabs.js';
import { FormDialog, SecretRevealDialog } from '../components/dialogs.js';
import { Field } from '../components/form-fields.js';
import { DetailSkeleton, PermissionDenied, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type { CursorPage, OpsSettings } from '../types/api.js';

const routeApi = getRouteApi(RID.settings);

export function SettingsPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canWrite = usePermission(OPS_PERMISSION.settingsWrite);

  const query = useQuery({
    queryKey: qk.settings(),
    // API はキー・値の平坦なリストを返す。境界（lib/settings-view.ts）で入れ子へ。
    queryFn: async () => toOpsSettings(await api.get<ApiSettingsList>('/settings')),
    enabled: search.tab !== 'account',
  });

  return (
    <>
      <PageHeader
        title="設定"
        description="コントロールプレーン全体の設定と、自分のアカウント。"
        actions={<RefetchingDot active={query.isFetching && !query.isLoading} />}
      />

      <PageTabs
        value={search.tab}
        onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
        tabs={[
          { value: 'general', label: '一般', icon: Settings2 },
          { value: 'acme', label: 'ACME', icon: Lock },
          { value: 'notification', label: '通知', icon: Bell },
          { value: 'webmail', label: 'Webメール連携', icon: Palette },
          { value: 'rollout', label: '配信の既定値', icon: Rocket },
          { value: 'account', label: 'アカウント', icon: UserRound },
        ]}
      />

      {search.tab === 'account' ? (
        <AccountTab />
      ) : query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isLoading ? (
        <DetailSkeleton />
      ) : query.data ? (
        <PlatformTabs tab={search.tab} settings={query.data} canWrite={canWrite} />
      ) : null}
    </>
  );
}

/* ---------------------------------------------------- プラットフォーム設定 */

function PlatformTabs({
  tab,
  settings,
  canWrite,
}: {
  tab: string;
  settings: OpsSettings;
  canWrite: boolean;
}): React.ReactElement {
  const qc = useQueryClient();
  const [draft, setDraft] = React.useState<OpsSettings>(settings);

  // サーバーの値が変わったら下書きを作り直す（別タブで保存された場合など）。
  React.useEffect(() => setDraft(settings), [settings]);

  const save = useMutation({
    // 更新は 1 キー単位（`PUT /settings/:key`）。変更されたキーだけ送る。
    mutationFn: () => saveOpsSettings(settings, draft),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.settings() }),
  });

  if (!canWrite) return <PermissionDenied />;

  const saveBar = (
    <div className="flex items-center gap-3">
      {save.isError ? (
        <span className="text-sm text-destructive">保存に失敗しました</span>
      ) : save.isSuccess ? (
        <span className="text-sm text-success">保存しました</span>
      ) : null}
      <Button onClick={() => save.mutate()} loading={save.isPending}>
        保存する
      </Button>
    </div>
  );

  if (tab === 'general') {
    return (
      <Section title="一般" actions={saveBar}>
        <div className="max-w-xl space-y-4 rounded-lg border border-border bg-card p-4">
          <Field label="サービス名" hint="ログイン画面とヘッダに表示されます。">
            <Input
              value={draft.general.brandName}
              onChange={(e) => setDraft({ ...draft, general: { ...draft.general, brandName: e.target.value } })}
            />
          </Field>
          <Field label="サポート連絡先メール">
            <Input
              type="email"
              value={draft.general.supportEmail}
              onChange={(e) => setDraft({ ...draft, general: { ...draft.general, supportEmail: e.target.value } })}
            />
          </Field>
          <Field label="サポート URL">
            <Input
              value={draft.general.supportUrl ?? ''}
              onChange={(e) => setDraft({ ...draft, general: { ...draft.general, supportUrl: e.target.value || null } })}
            />
          </Field>
        </div>
      </Section>
    );
  }

  if (tab === 'acme') {
    return (
      <Section title="ACME（証明書の自動取得）" actions={saveBar}>
        <div className="max-w-xl space-y-4 rounded-lg border border-border bg-card p-4">
          <Field label="ディレクトリ URL" hint="Let's Encrypt の staging と production を取り違えないこと。">
            <Input
              value={draft.acme.directoryUrl}
              onChange={(e) => setDraft({ ...draft, acme: { ...draft.acme, directoryUrl: e.target.value } })}
              className="font-mono text-xs"
            />
          </Field>
          <Field label="連絡先メール" hint="失効・期限切れの通知先として CA に登録されます。">
            <Input
              type="email"
              value={draft.acme.contactEmail}
              onChange={(e) => setDraft({ ...draft, acme: { ...draft.acme, contactEmail: e.target.value } })}
            />
          </Field>
          <Field label="EAB Key ID" hint="外部アカウント連携が必要な CA を使う場合のみ。">
            <Input
              value={draft.acme.eabKid ?? ''}
              onChange={(e) => setDraft({ ...draft, acme: { ...draft.acme, eabKid: e.target.value || null } })}
            />
          </Field>
          <Field label="更新開始（期限の何日前）">
            <Input
              type="number"
              min={1}
              max={60}
              value={draft.acme.renewBeforeDays}
              onChange={(e) => setDraft({ ...draft, acme: { ...draft.acme, renewBeforeDays: Number(e.target.value) } })}
            />
          </Field>
        </div>
      </Section>
    );
  }

  if (tab === 'notification') {
    return (
      <Section title="通知" actions={saveBar}>
        <div className="max-w-xl space-y-4 rounded-lg border border-border bg-card p-4">
          <Field label="アラート送信先（カンマ区切り）">
            <Input
              value={draft.notification.alertEmails.join(', ')}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  notification: {
                    ...draft.notification,
                    alertEmails: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter((s) => s.length > 0),
                  },
                })
              }
            />
          </Field>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Slack Webhook</span>
            <Badge variant={draft.notification.slackWebhookConfigured ? 'success' : 'muted'} size="sm">
              {draft.notification.slackWebhookConfigured ? '設定済み' : '未設定'}
            </Badge>
            <span className="text-xs text-muted-foreground">
              （URL は環境変数で設定します。この画面には出しません）
            </span>
          </div>
          <div className="divide-y divide-border">
            {Object.entries(draft.notification.events).map(([key, on]) => (
              <SwitchRow
                key={key}
                label={key}
                checked={on}
                onCheckedChange={(v) =>
                  setDraft({
                    ...draft,
                    notification: { ...draft.notification, events: { ...draft.notification.events, [key]: v } },
                  })
                }
              />
            ))}
          </div>
        </div>
      </Section>
    );
  }

  if (tab === 'webmail') {
    return (
      <Section title="aurora-mail 連携" actions={saveBar}>
        <div className="max-w-xl space-y-4 rounded-lg border border-border bg-card p-4">
          <Field label="Webメールの公開 URL">
            <Input
              value={draft.webmail.baseUrl}
              onChange={(e) => setDraft({ ...draft, webmail: { ...draft.webmail, baseUrl: e.target.value } })}
            />
          </Field>
          <Field label="Admin API のベース URL">
            <Input
              value={draft.webmail.adminApiUrl}
              onChange={(e) => setDraft({ ...draft, webmail: { ...draft.webmail, adminApiUrl: e.target.value } })}
              className="font-mono text-xs"
            />
          </Field>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">API キー</span>
            <code className="rounded bg-panel px-2 py-1 font-mono text-xs">
              {draft.webmail.apiKeyMasked ?? '未設定'}
            </code>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <StatusBadge status={draft.webmail.syncState} size="sm" dot />
            <span className="text-xs text-muted-foreground">
              最終同期 {formatRelative(draft.webmail.lastSyncAt)}
            </span>
            <WebmailDriftButton />
          </div>
        </div>
      </Section>
    );
  }

  if (tab === 'rollout') {
    return (
      <Section title="配信の既定値" actions={saveBar}>
        <div className="max-w-xl space-y-4 rounded-lg border border-border bg-card p-4">
          <Field label="canary の soak（分）" hint="この時間だけ様子を見てから次のリングへ進みます。">
            <Input
              type="number"
              min={0}
              value={draft.rollout.defaultSoakMinutes.canary}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  rollout: {
                    ...draft.rollout,
                    defaultSoakMinutes: { ...draft.rollout.defaultSoakMinutes, canary: Number(e.target.value) },
                  },
                })
              }
            />
          </Field>
          <Field label="ring1 の soak（分）">
            <Input
              type="number"
              min={0}
              value={draft.rollout.defaultSoakMinutes.ring1}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  rollout: {
                    ...draft.rollout,
                    defaultSoakMinutes: { ...draft.rollout.defaultSoakMinutes, ring1: Number(e.target.value) },
                  },
                })
              }
            />
          </Field>
          <Field label="同時停止の上限（%）">
            <Input
              type="number"
              min={1}
              max={100}
              value={draft.rollout.maxUnavailablePct}
              onChange={(e) =>
                setDraft({ ...draft, rollout: { ...draft.rollout, maxUnavailablePct: Number(e.target.value) } })
              }
            />
          </Field>
        </div>
      </Section>
    );
  }

  return <PermissionDenied />;
}

function WebmailDriftButton(): React.ReactElement {
  const check = useMutation({
    // API は差分行そのもの（cursor 一覧）を返す。件数だけ使う。
    mutationFn: async () => {
      const res = await api.get<CursorPage<unknown>>('/webmail/drift', { limit: 200 });
      return { drifted: res.items.length };
    },
  });
  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="xs" onClick={() => check.mutate()} loading={check.isPending}>
        差分を確認
      </Button>
      {check.data ? (
        <span className={check.data.drifted > 0 ? 'text-xs text-warning' : 'text-xs text-success'}>
          差分 {check.data.drifted} 件
        </span>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------ アカウント */

function AccountTab(): React.ReactElement {
  const { user } = useAuth();

  const sessions = useQuery({
    queryKey: qk.sessions(),
    queryFn: fetchSessions,
  });

  const revokeOthers = useMutation({
    mutationFn: revokeOtherSessions,
    onSuccess: () => void sessions.refetch(),
  });

  return (
    <div className="space-y-6">
      <Section title="アカウント">
        <div className="max-w-xl space-y-3 rounded-lg border border-border bg-card p-4 text-sm">
          <p>
            <span className="text-muted-foreground">メールアドレス</span>
            <span className="ml-2 font-medium">{user?.email}</span>
          </p>
          <p>
            <span className="text-muted-foreground">ロール</span>
            <span className="ml-2">{user?.role}</span>
          </p>
          <div className="flex items-center gap-2 pt-2">
            <PasswordChangeButton />
            <TotpButton enrolled={user?.mfaEnabled ?? false} />
          </div>
        </div>
      </Section>

      {!user?.mfaEnabled ? (
        <Alert variant="destructive">
          <AlertTitle>2 要素認証が未登録です</AlertTitle>
          <AlertDescription>
            ops 面は TOTP 必須です。登録が済むまで、この画面以外の操作はすべて拒否されます。
          </AlertDescription>
        </Alert>
      ) : null}

      {/*
        1 行 = **1 回のログイン**。サーバが `family_id` に畳んで返す。
        以前は refresh のローテーションで増えた行をそのまま並べていたため、
        数回しかログインしていない運営者の一覧が 60 行になっていた。
        「終了」の列は無い（他は一括失効で落とす）。**状態の列は必要**で、
        失効済み・期限切れの行が「現在ログイン中」に見えてはいけない。
      */}
      <Section
        title="ログイン"
        description="1 行が 1 回のログインです。同じログインの中でトークンは定期的に更新されます。"
        actions={
          <Button variant="outline" size="sm" onClick={() => revokeOthers.mutate()} loading={revokeOthers.isPending}>
            他のセッションを失効
          </Button>
        }
      >
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">状態</TableHead>
                <TableHead scope="col">IP</TableHead>
                <TableHead scope="col">ブラウザ</TableHead>
                <TableHead scope="col">開始</TableHead>
                <TableHead scope="col">最終利用</TableHead>
                <TableHead scope="col" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(sessions.data ?? []).map((s: SessionRow) => (
                <TableRow key={s.id}>
                  <TableCell>
                    <Badge variant={s.active ? 'success' : 'muted'} size="sm">
                      {s.active ? '有効' : '終了'}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{s.ip ?? '—'}</TableCell>
                  <TableCell className="max-w-xs truncate text-xs text-muted-foreground">{s.userAgent ?? '—'}</TableCell>
                  <TableCell className="text-xs">
                    {formatDateTime(s.createdAt)}
                    {s.rotations > 1 ? (
                      <span className="ml-1 text-muted-foreground">（更新 {s.rotations} 回）</span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-xs">{formatRelative(s.lastSeenAt)}</TableCell>
                  <TableCell>
                    {s.current ? (
                      <Badge variant="success" size="sm">
                        現在
                      </Badge>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Section>

      <Section title="外観" description="テーマはこのブラウザにだけ保存されます。">
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-4">
          <ThemeToggle />
          <span className="text-sm text-muted-foreground">テーマを切り替える</span>
        </div>
      </Section>
    </div>
  );
}

function PasswordChangeButton(): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [current, setCurrent] = React.useState('');
  const [next, setNext] = React.useState('');
  const [confirm, setConfirm] = React.useState('');

  const mut = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => setOpen(false),
  });

  React.useEffect(() => {
    if (open) {
      setCurrent('');
      setNext('');
      setConfirm('');
    }
  }, [open]);

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <KeyRound aria-hidden />
        パスワードを変更
      </Button>
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title="パスワードを変更"
        description="変更すると、このセッション以外はすべて失効します。"
        submitLabel="変更する"
        pending={mut.isPending}
        error={mut.isError ? mut.error : undefined}
        disabled={next.length < 12 || next !== confirm}
        onSubmit={() => mut.mutate()}
      >
        <Field label="現在のパスワード" required>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
        </Field>
        <Field label="新しいパスワード" required hint="12 文字以上。">
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
        </Field>
        <Field
          label="新しいパスワード（確認）"
          required
          error={confirm.length > 0 && confirm !== next ? '一致しません' : undefined}
        >
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </Field>
      </FormDialog>
    </>
  );
}

function TotpButton({ enrolled }: { enrolled: boolean }): React.ReactElement {
  const qc = useQueryClient();
  const [setup, setSetup] = React.useState<TotpSetup | null>(null);
  const [code, setCode] = React.useState('');
  const [recovery, setRecovery] = React.useState<string[] | null>(null);
  /** サーバがセッションを差し替えられなかった場合だけ true（再ログインが要る）。 */
  const [needsRelogin, setNeedsRelogin] = React.useState(false);

  const start = useMutation({
    mutationFn: setupTotp,
    onSuccess: (res) => {
      setSetup(res);
      setCode('');
    },
  });

  /**
   * 登録の確定。
   *
   * `verifyTotpSetup()` が **新しいアクセストークンの載せ替えと `/me` の
   * 引き直し**まで行う。ここでさらにキャッシュを捨てるのは、登録前に
   * `MFA_REQUIRED` で失敗して残っているクエリ（ダッシュボード等）を
   * 新しいトークンで取り直させるため。捨てないと、2 要素へ上がった後も
   * 画面には「読み込みに失敗しました」が残り続ける。
   */
  const verify = useMutation({
    mutationFn: () => verifyTotpSetup(code.trim()),
    onSuccess: (res) => {
      setSetup(null);
      setRecovery(res.recoveryCodes);
      setNeedsRelogin(!res.accessToken);
      void qc.invalidateQueries();
    },
  });

  return (
    <>
      <Button variant={enrolled ? 'outline' : 'default'} size="sm" onClick={() => start.mutate()} loading={start.isPending}>
        <Lock aria-hidden />
        {enrolled ? '2FA を再設定' : '2FA を登録'}
      </Button>

      <FormDialog
        open={setup !== null}
        onOpenChange={(next) => {
          if (!next) setSetup(null);
        }}
        title="2 要素認証を登録"
        description="認証アプリで QR を読み取り、表示された 6 桁のコードを入力してください。"
        submitLabel="登録する"
        pending={verify.isPending}
        error={verify.isError ? verify.error : undefined}
        disabled={code.trim().length !== 6}
        onSubmit={() => verify.mutate()}
      >
        {setup ? (
          <>
            {/*
              QR は API が返す **PNG の data URI**（`qrDataUrl`）をそのまま使う。
              自前で base64 に組み直さない（以前は存在しない `qrSvg` を
              エンコードしていて壊れた data URI ができていた）。PNG なので
              SVG のようにスクリプトを持ち込めない。
            */}
            <img
              src={setup.qrDataUrl}
              alt="2 要素認証の QR コード"
              className="mx-auto w-40 rounded-lg bg-white p-2"
            />
            <Field label="手入力用のシークレット">
              <Input readOnly value={setup.secret} className="font-mono text-xs" />
            </Field>
            <Field label="認証コード（6 桁）" required>
              <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" />
            </Field>
          </>
        ) : null}
      </FormDialog>

      <SecretRevealDialog
        open={recovery !== null}
        onOpenChange={() => setRecovery(null)}
        title="リカバリコード"
        description={
          needsRelogin
            ? '認証アプリを失ったときの最後の手段です。今すぐ安全な場所へ保管してください。再表示できません。' +
              '（このセッションは二要素認証へ切り替えられませんでした。保管し終えたら、いったんログアウトして入り直してください。）'
            : '認証アプリを失ったときの最後の手段です。今すぐ安全な場所へ保管してください。再表示できません。'
        }
        items={(recovery ?? []).map((c, i) => ({ label: `コード ${i + 1}`, value: c }))}
      />
    </>
  );
}
